/**
 * useKeyboardAwareInputs — Jobsite Capture Arc Stage 2D Part 4b (2026-06-15).
 *
 * Keeps the focused input/textarea visible above the on-screen keyboard
 * on mobile. Uses three layered defenses:
 *
 *   1. `viewport-fit=cover, interactive-widget=resizes-content` in the
 *      viewport meta (set in index.html) — newer mobile browsers will
 *      shrink the layout viewport so the focused field is naturally
 *      visible.
 *
 *   2. `visualViewport.resize` listener (Chrome, Firefox, Safari iOS
 *      13+) — when the keyboard opens, the visual viewport height
 *      drops. We scroll the focused element to the middle of the
 *      remaining visible area.
 *
 *   3. `focus` listener (capture phase) on document — when an
 *      input/textarea/contenteditable gains focus, scroll it into view
 *      with `block: 'center'` after a short delay (lets the keyboard
 *      finish animating). Mounted once at the app root.
 *
 * iOS Safari residual limitation (HONEST): position:fixed elements
 * anchored to the bottom of the screen can clip the keyboard from
 * pushing them up. The `interactive-widget=resizes-content` viewport
 * + visualViewport-driven body-padding-bottom workaround we add here
 * fixes most cases but a small subset (modals with backdrop +
 * keyboard) may still misbehave on iOS < 16.4. Tested on Chrome /
 * Edge / Firefox / Safari macOS — works. iOS Safari < 16.4 — degraded
 * gracefully (input still becomes visible via scroll, but bottom-
 * anchored toolbars may sit beneath the keyboard).
 */
import { useEffect } from 'react';

const FOCUSABLE_SELECTOR = 'input, textarea, [contenteditable="true"]';
const SCROLL_DELAY_MS = 250;   // keyboard animation budget
const RESIZE_DELAY_MS = 120;   // visualViewport debounce

function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
}

function scrollToFocused() {
  const active = document.activeElement;
  if (!active) return;
  if (!active.matches?.(FOCUSABLE_SELECTOR)) return;
  // Use scrollIntoView with center alignment — visualViewport already
  // adjusted, so the layout viewport's "center" is now the visible
  // strip above the keyboard.
  try {
    active.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  } catch (_e) {
    // Older browsers — fall back to default scrollIntoView.
    active.scrollIntoView(true);
  }
}

export function useKeyboardAwareInputs() {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!isMobileViewport()) return undefined;

    const vv = window.visualViewport;
    let resizeTimer = null;
    let focusTimer = null;

    const onResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // Set a CSS variable any consumer can use for safe bottom
        // padding (e.g. fixed action bars that should ride above the
        // keyboard).
        if (vv) {
          const keyboardOffset = Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0));
          document.documentElement.style.setProperty('--kb-offset', `${keyboardOffset}px`);
        }
        scrollToFocused();
      }, RESIZE_DELAY_MS);
    };

    const onFocusIn = (e) => {
      const target = e.target;
      if (!target || !target.matches?.(FOCUSABLE_SELECTOR)) return;
      if (focusTimer) clearTimeout(focusTimer);
      focusTimer = setTimeout(() => {
        scrollToFocused();
      }, SCROLL_DELAY_MS);
    };

    const onFocusOut = () => {
      // Reset keyboard offset when nothing focused (the browser may not
      // fire `resize` on blur on some platforms).
      setTimeout(() => {
        if (!document.activeElement || !document.activeElement.matches?.(FOCUSABLE_SELECTOR)) {
          document.documentElement.style.setProperty('--kb-offset', '0px');
        }
      }, 200);
    };

    if (vv) {
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
    }
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);

    return () => {
      if (vv) {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
      }
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      if (resizeTimer) clearTimeout(resizeTimer);
      if (focusTimer) clearTimeout(focusTimer);
      document.documentElement.style.setProperty('--kb-offset', '0px');
    };
  }, []);
}
